// Foundations
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
} from '@angular/forms';

import { ReservationRequestItem } from 'src/app/models/reservation/reservation-request.model';
import { ReservationServiceCategory } from 'src/app/models/reservation/reservation-service-category.model';

@Component({
  selector: 'app-reservation-service-tile-form',
  templateUrl: './reservation-service-tile-form.component.html',
  styleUrls: ['./reservation-service-tile-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReservationServiceTileFormComponent
  implements OnInit, AfterViewChecked
{
  // #region -> Property - Component IO <-
  // ----------

  @Input() formGroup: FormGroup;
  @Input() serviceCategory: ReservationServiceCategory;
  @Input() reservationRequest: ReservationRequestItem;
  @Input() isErrorOnSave: boolean;

  @Output() selectedServices = new EventEmitter<ReservationRequestItem>();

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Form <-
  // ----------

  /**
   * The form array of the services.
   */
  get serviceFormArray(): FormArray {
    return this.formGroup.get('serviceFormArray') as FormArray;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef
  )
  {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    this.formGroup.patchValue({
      name: [this.serviceCategory.categoryName],
      // description: [this.serviceCategory.description],
      services: [this.serviceCategory.coreServices],
    });
  }

  ngAfterViewChecked(): void { }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------

  canCategoryBeSelected(): boolean {
    //
    return this.serviceCategory.canBeSelectable(
      this.reservationRequest);
  }

  toggleCategory(): void {
    //
    if (!this.formGroup.controls.isSelected.value) {
      // About to be selected, auto select the first item
      this.serviceFormArray.at(0).patchValue({
        isSelected: true,
      });
      this.reservationRequest = this.reservationRequest.addNewCoreService(
        this.serviceFormArray.at(0).value.linkedService
      );

      // Adjust the price
      this.formGroup.patchValue({
        totalPrice: [+this.formGroup.controls.totalPrice.value + +this.serviceFormArray.at(0).value.linkedService.servicePrice],
      });
    } else {
      // Remove all selections
      this._deselectFormElement();
    }

    //
    this.formGroup.patchValue({
      isSelected: !this.formGroup.controls.isSelected.value,
    });
    this.selectedServices.emit(this.reservationRequest);
  }

  /**
   * When a service is selected.
   *
   * Need to cover the following special cases:
   * - An item can be "de-selected"
   * - If a category does not have any selected service, it should be also de-selected.
   *
   * @param index Form element index
   */
  toggleService(index: number): void {
    //
    const targetElement = this.serviceFormArray.at(index);
    if (targetElement.value.isSelected) {
      // de-select
      this._deselectFormElement(targetElement);

      // Check if this "category" can be also de-selected
      if (
        !this.reservationRequest.includeCoreCategory(
          this.serviceCategory.categoryId
        )
      ) {
        // De-select the category
        this.formGroup.patchValue({
          isSelected: !this.formGroup.controls.isSelected.value
        });
      }
    } else {
      // Handle "adding"
      if (!this.serviceCategory.multiSelectable) {
        // Single selection
        // Firstly remove all current selections
        this._deselectFormElement();
      }

      // Add the new element
      targetElement.patchValue({
        isSelected: !targetElement.value.isSelected,
      });
      this.reservationRequest = this.reservationRequest.addNewCoreService(
        targetElement.value.linkedService
      );

      // Adjust the price
      this.formGroup.patchValue({
        totalPrice: [+this.formGroup.controls.totalPrice.value + +targetElement.value.linkedService.servicePrice],
      });
    }

    //
    this.selectedServices.emit(this.reservationRequest);
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _deselectFormElement(targetElement?: AbstractControl): void {
    //
    if (targetElement) {
      // Just de-select a specific one
      this.serviceFormArray.controls.forEach((element) => {
        //
        if (element.value.id === targetElement.value.id) {
          // Remove
          element.patchValue({
            isSelected: false,
          });
          this.reservationRequest = this.reservationRequest.removeCoreService(
            element.value.linkedService
          );

          //
          this.formGroup.patchValue({
            totalPrice: [+this.formGroup.controls.totalPrice.value - +targetElement.value.linkedService.servicePrice],
          });
        }
      });
    } else {
      // De-select "ALL"
      this.serviceFormArray.controls.forEach((element) => {
        //
        if (element.value.isSelected) {
          // Remove
          element.patchValue({
            isSelected: false,
          });
          this.reservationRequest = this.reservationRequest.removeCoreService(
            element.value.linkedService
          );
        }
      });

      // Reset the price
      this.formGroup.patchValue({
        totalPrice: [0],
      });
    }
  }

  // ----------
  // #endregion

  /////////////////////////////
}
